import type { ReactNode } from "react";

import { Blurred } from "../components/Blurred";
import { Badge, Card } from "../components/ui";
import { PRODUCTS, productById, type ProductId } from "./registry";

/**
 * How one product's half of a shared surface is drawn.
 *
 * The design puts both products on the same row rather than behind a switcher,
 * and that is the right call: the fact worth surfacing is DIVERGENCE — a
 * merchant sitting on `labeeb:standard` and `mcp:premium` — and a switcher
 * shows one side at a time, which is precisely how a split goes unnoticed for
 * a billing cycle. Everything here therefore renders every product at once.
 *
 * An unwired product's cell says "not wired", never "—" and never blank.
 * Those already mean something specific in this console: "—" is a value the
 * product does not store, and blank is a field that did not load. Reusing
 * either would answer a question the console cannot answer, and the operator
 * would act on it.
 */

/** Which product a surface or panel belongs to. */
export function ProductTag({ id }: { id: ProductId }) {
  const p = productById(id);
  if (!p) return null;
  return (
    <span title={p.declared ? `${p.apiPrefix}/admin/*` : `Not wired — needs ${p.needs}`}>
      <Badge tone={p.declared ? "neutral" : "amber"}>{p.label}</Badge>
    </span>
  );
}

/**
 * The design's "Stored as" cell — one mono line per product, in registry order.
 *
 * THREE STATES, and they are not interchangeable:
 *   not wired   the platform has no route for this product (Labeeb, today)
 *   no row      the product is reachable and has stored nothing
 *   a value     what is actually stored
 *
 * The middle one is why this cell exists next to a Tier column: the server
 * resolves an absent MCP row AS free, so "free" and "nothing stored" look
 * identical downstream and differ completely in what an operator should do.
 */
export function StoredAs({ stored }: { stored: Partial<Record<ProductId, string | null>> }) {
  return (
    <div className="font-mono text-xs text-slate-500 dark:text-slate-400">
      {PRODUCTS.map((p) => (
        <div key={p.id}>
          {p.id}:
          {!p.declared ? (
            <span
              className="text-slate-400 italic dark:text-slate-500"
              title={`Not wired — needs ${p.needs}`}
            >
              not wired
            </span>
          ) : (
            (stored[p.id] ?? <span className="text-slate-400 dark:text-slate-500">no row</span>)
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * A panel that belongs to one product, blurred when that product is unwired.
 *
 * This is the per-product form of the coming-soon treatment: the panel keeps
 * its place and its shape, carries the badge saying whose it is, and states
 * what has to exist. `children` is a shape-only placeholder for an unwired
 * product — never real-looking data. See `Blurred`.
 */
export function ProductPanel({
  id,
  title,
  what,
  note,
  children,
  minHeight = 180,
}: {
  id: ProductId;
  title: string;
  /** What the panel will show, for the overlay. Ignored when wired. */
  what?: string;
  /** One line under the title, before the body. */
  note?: ReactNode;
  children: ReactNode;
  minHeight?: number;
}) {
  const p = productById(id);
  if (!p) return null;

  return (
    // The design's card, with the owning product where it puts the
    // "read-only" badge. Same surface as every other panel in the console —
    // a blurred panel that also had its own shape would read as a different
    // kind of thing rather than the same thing, waiting.
    <Card title={title} note={note} aside={<ProductTag id={id} />}>
      {p.declared ? (
        children
      ) : (
        <Blurred what={what ?? title} needs={p.needs ?? "an admin API"} minHeight={minHeight}>
          {children}
        </Blurred>
      )}
    </Card>
  );
}

/**
 * The inline flag for a value an unwired product would supply.
 *
 * Sized to sit inside a table cell or a field row, where a full Blurred panel
 * would break the grid. Carries the same information in one line.
 */
export function NotWired({ id, what }: { id: ProductId; what: string }) {
  const p = productById(id);
  if (!p) return null;
  return (
    <span
      className="text-xs text-slate-400 dark:text-slate-500"
      title={`${what} — needs ${p.needs}`}
    >
      {p.label} not wired
    </span>
  );
}

/**
 * Every product's coverage of one capability, as a compact list.
 *
 * Used where a surface is wholly one product's — Tool caps is MCP's, HITL
 * gates are Labeeb's — so the reader learns the scope of what they are looking
 * at rather than assuming it covers the fleet's whole AI surface.
 */
export function ProductScope({ owns, children }: { owns: ProductId[]; children?: ReactNode }) {
  return (
    // A LINE, not a panel. This is a scope note on the list below it, and a
    // ringed box above every table made the chrome taller than the first row
    // of data on four pages.
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
      <span>Applies to</span>
      {PRODUCTS.map((p) => (
        <span key={p.id} className={owns.includes(p.id) ? "" : "opacity-40"}>
          <ProductTag id={p.id} />
        </span>
      ))}
      {children && <span className="min-w-0">— {children}</span>}
    </p>
  );
}
